import React from 'react';
import { Play, FileText, CheckCircle, ChevronDown, ChevronRight, Share2, X, HelpCircle } from 'lucide-react';
import { Progress } from '../ui/progress';

// LinkedIn-Learning-style "Course contents" rail: module accordions with
// lesson rows, completion checkmarks and a now-playing highlight.
export const CourseContents = ({
  curriculum,
  stats,
  expandedModules,
  onToggleModule,
  onShareModule,
  onSelectLesson,
  currentLessonId,
  onClose,
}) => (
  <aside className="bg-brand-surface border border-white/5 rounded-xl overflow-hidden flex flex-col" data-testid="course-contents">
    <div className="p-4 border-b border-white/5 flex items-center justify-between">
      <div>
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Course contents</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          {stats?.completed_lessons || 0}/{stats?.total_lessons || 0} lessons · {Math.round(stats?.progress_percent || 0)}% complete
        </p>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="p-1.5 text-gray-400 hover:text-white transition-colors"
          aria-label="Hide course contents"
          data-testid="contents-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
    <Progress value={stats?.progress_percent || 0} className="h-1 rounded-none" />

    <div className="overflow-y-auto flex-1 divide-y divide-white/5">
      {curriculum?.map((module, mi) => (
        <div key={module.id} data-testid={`module-${module.id}`}>
          <div className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/[0.03] transition group">
            <button
              onClick={() => onToggleModule(module.id)}
              className="flex-1 text-left min-w-0"
              data-testid={`module-toggle-${module.id}`}
            >
              <p className="text-sm font-medium text-white truncate group-hover:text-brand-gold transition-colors">
                {mi + 1}. {module.title}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {module.completed_count}/{module.lessons.length} done · {module.total_duration} min
              </p>
            </button>
            <div className="flex items-center flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); onShareModule(module.id, module.title); }}
                className="p-1.5 text-gray-500 hover:text-brand-gold transition-colors"
                title="Copy shareable link"
                data-testid={`module-share-${module.id}`}
              >
                <Share2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onToggleModule(module.id)}
                className="p-1.5 text-gray-500 hover:text-white transition-colors"
                aria-label="Toggle module"
              >
                {expandedModules[module.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {expandedModules[module.id] && (
            <div className="bg-black/30">
              {module.lessons.map((lesson) => {
                const active = lesson.id === currentLessonId;
                return (
                  <button
                    key={lesson.id}
                    onClick={() => onSelectLesson(lesson)}
                    className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors border-l-2 ${
                      active ? 'border-brand-gold bg-brand-gold/10' : 'border-transparent hover:bg-white/[0.03]'
                    }`}
                    data-testid={`lesson-row-${lesson.id}`}
                  >
                    {lesson.is_completed ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    ) : lesson.content_type === 'quiz' ? (
                      <HelpCircle className={`w-4 h-4 flex-shrink-0 ${active ? 'text-brand-gold' : 'text-gray-500'}`} />
                    ) : lesson.content_type === 'video' ? (
                      <Play className={`w-4 h-4 flex-shrink-0 ${active ? 'text-brand-gold fill-current' : 'text-gray-500'}`} />
                    ) : (
                      <FileText className={`w-4 h-4 flex-shrink-0 ${active ? 'text-brand-gold' : 'text-gray-500'}`} />
                    )}
                    <span
                      className={`flex-1 text-sm truncate ${
                        active ? 'text-brand-gold font-medium' : lesson.is_completed ? 'text-gray-400' : 'text-gray-200'
                      }`}
                    >
                      {lesson.title}
                    </span>
                    <span className="text-[11px] text-gray-500 flex-shrink-0">{lesson.duration_minutes}m</span>
                    {lesson.has_quiz && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                          lesson.quiz_passed ? 'bg-emerald-500/15 text-emerald-400' : 'bg-brand-gold/15 text-brand-gold'
                        }`}
                      >
                        Quiz
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  </aside>
);
